import * as FileSaver from "file-saver";
import { Workbook, Worksheet } from "exceljs";
import i18n from "i18n";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import { ProfileService } from "modules/profile/ProfileService";
import { DevelopmentStore } from "modules/development/DevelopmentStore";
import { CustomPropertyStore } from "modules/customProperties/CustomPropertyStore";
import {
  borderExpressionStyle,
  borderThickStyle,
  fillDarkGray,
  fillGreen,
  fillGray,
  fontBold,
  fontTitleHeader,
  fontToDarkFill,
} from "./ReportStyles";
import { createAlphabet, getLetterFrom } from "./ReportUtils";
import {
  alphabet,
  firstRowHeight,
  monetaryFormat,
  secondRowHeight,
} from "./constants";
import rubkPng from "common/assets/images/rubk.png";
import { EReportType } from "common/enums";

// Relatório da planilha principal
import getDataEAP from "./eapCustom";
// Relatório da planilha EAP analitica
import getDataEAPAnalytical from "./eapAnalyticalCustom";
// Relatório da planilha de insumos
import getDataResource from "./resourceCustom";
// Relatório da planilha de formação
import getDataFormating from "./formatingCustom";
// Relatório da planilha de insumos
import getDataTakeoff from "./takeoffCustom";

// Relatórios da planilha de insumos agrupados sintético e analítico
import getDataGroupedResourcesSynthetic from "./groupedResourcesSynthetic";
import getDataGroupedResourcesAnalytic from "./groupedResourcesAnalytic";

//Relatórios de curva ABC de serviços sintético e analítico
import {
  getDataToSyntheticABCCurveServiceReport,
  getDataToAnalyticABCCurveServiceReport,
} from "./abcCurveService";

const ExcelJS = require("exceljs");

const columnsOffset = 1.24;

const labels = i18n.syntheticReport;
const sideMenuLabels = i18n.project.rightSideMenu.WBSInfo;

export async function exportSyntheticEAPReport() {
  const name =
    i18n.syntheticReport.budgetSynthetic + i18n.syntheticReport.budget;
  const title = labels.reportTitle1 + name.toUpperCase() + labels.reportTitle2;

  await createExcel(EReportType.EAP, name, title, "R", true, true);
}

export async function exportAnalyticalEAPReport() {
  const name =
    i18n.syntheticReport.budgetAnalytical + i18n.syntheticReport.budget;
  const title = labels.reportTitle1 + name.toUpperCase() + labels.reportTitle2;

  await createExcel(EReportType.EAP_ANALYTICAL, name, title, "S", true, true);
}

export async function exportSyntheticCurveABCReport() {
  const name = i18n.wbs.nameReportCurveABC;
  const title = labels.reportTitle1 + name.toUpperCase() + labels.reportTitle2;

  await createExcel(
    EReportType.CURVE_ABC_SYNTHETIC,
    name,
    title,
    "J",
    true,
    false,
    i18n.wbs.totalCostReportCurveABC
  );
}

export async function exportAnalyticalCurveABCReport() {
  const name = i18n.wbs.nameReportCurveABCAnalytical;
  const title = labels.reportTitle1 + name.toUpperCase() + labels.reportTitle2;

  await createExcel(
    EReportType.CURVE_ABC_ANALYTICAL,
    name,
    title,
    "J",
    true,
    false,
    i18n.wbs.totalCostReportCurveABC
  );
}

export async function exportResourceReport() {
  const name = i18n.syntheticReport.resources;
  const title = labels.reportTitle1 + name.toUpperCase() + labels.reportTitle2;

  await createExcel(EReportType.RESOURCE, name, title, "L", true);
}

export async function exportSyntheticGroupedResourceReport(
  isType: boolean = false,
  propertyKey?: string
) {
  const name = i18n.syntheticReport.resources;
  const title = labels.groupedResourcesSynthetic;

  await createExcel(
    EReportType.GROUPED_RESOURCES_SYNTHETIC,
    name,
    title,
    "L",
    false,
    false,
    "",
    isType,
    propertyKey
  );
}

export async function exportAnalyticalGroupedResourceReport(
  isType: boolean = false,
  propertyKey?: string
) {
  const name = i18n.syntheticReport.resources;
  const title = labels.groupedResourcesAnalytical;

  await createExcel(
    EReportType.GROUPED_RESOURCES_ANALYTICAL,
    name,
    title,
    "L",
    false,
    false,
    "",
    isType,
    propertyKey
  );
}

export async function exportTakeOffReport(takeOffName: string) {
  const name = takeOffName + " - " + i18n.syntheticReport.takeoff;
  const title = labels.reportTitle1 + name.toUpperCase() + labels.reportTitle2;

  await createExcel(EReportType.TAKEOFF, name, title, "L");
}

export async function exportTakeOffFormatingReport(takeOffName: string) {
  const name = takeOffName + " - " + i18n.syntheticReport.formating;
  const title = labels.reportTitle1 + name.toUpperCase() + labels.reportTitle2;

  await createExcel(EReportType.FORMATING, name, title, "L");
}

/** Monta a planilha do Excel para exportação de todos os tipos de relatórios. */
async function createExcel(
  reportType: EReportType,
  name: string,
  title: string,
  finalColumn: string,
  showCustomProperties: boolean = false,
  showBDITotal: boolean = false,
  totalLabel: string = "",
  isType: boolean = false,
  propertyKey?: string
) {
  const { workbook, worksheet } = createWorks(name);

  let finalHeaderColumn = finalColumn;
  if (showBDITotal && finalColumn < "O") finalHeaderColumn = "O";
  else if (finalColumn < "J") finalHeaderColumn = "J";

  let logoRangeCells = "I1:J1";
  if (finalColumn !== "J") {
    const initialLogoColumn = getLetterFrom(finalHeaderColumn, -2);
    logoRangeCells = `${initialLogoColumn}1:${finalHeaderColumn}1`;
  }

  // Define estilos das células
  createHeader(worksheet, title, finalHeaderColumn, logoRangeCells);
  createDetailsColumn1(worksheet);
  createDetailsColumn2(worksheet, finalHeaderColumn, totalLabel, showBDITotal);
  createTitleRow(worksheet, finalColumn);

  if (showCustomProperties) {
    addCustomPropertiesColumns(worksheet, finalColumn);
  }

  setColumnsWidth(worksheet, finalColumn);
  setVerticalAlignment(worksheet);

  const company = await ProfileService.fetchCompanyByUid();
  let companyName = company ? company.name : "";
  let companyLogoURL = company ? company.image : "";

  // Adiciona os dados do relatório
  setCostPlanData(worksheet, companyName, showBDITotal);
  populateTable(worksheet, reportType, isType, propertyKey);

  // Adiciona imagem da Empresa
  await addCompanyImage(workbook, worksheet, companyLogoURL);
  // Adiciona logo do Rubk no cabeçalho
  addImageToWorksheet(workbook, worksheet, rubkPng, logoRangeCells);

  const nameFile = `${DevelopmentStore.developmentSelected.data.name} - ${name}.xlsx`;
  saveAs(workbook, nameFile);
}

export function createWorks(name: string) {
  const workbook: Workbook = new ExcelJS.Workbook();
  workbook.creator = "Rubk";
  workbook.created = new Date();

  const worksheet: Worksheet = workbook.addWorksheet(name, {
    pageSetup: { paperSize: 9, orientation: "landscape" },
  });

  return {
    workbook,
    worksheet,
  };
}

/** Cria o cabeçalho do relatório, definindo altura e estilo das células.
 * @param {Worksheet} worksheet Instância da planilha a ser exportada
 * @param {string} title Título do relatório que será colocado na 2ª linha
 * @param {string} finalColumn Coluna final que será preenchida com o cabeçalho do relatório
 */
export function createHeader(
  worksheet: Worksheet,
  title: string,
  finalColumn: string,
  logoRange: string
) {
  // Adiciona estilho da borda
  worksheet.addConditionalFormatting({
    ref: `A1:${finalColumn}2`,
    rules: borderExpressionStyle,
  });

  // Mescla as células utilizadas para adicionar a logo da empresa.
  worksheet.mergeCells("A1:C1");
  // Mescla as células utilizadas para adicionar a logo do Rubk.
  let columnBeforeLogo = getLetterFrom(logoRange.substring(0, 1), -1);
  worksheet.mergeCells(`D1:${columnBeforeLogo}1`);
  worksheet.mergeCells(logoRange);
  // Mescla as células do título do relatório
  worksheet.mergeCells(`A2:${finalColumn}2`);

  // Altera a altura das linhas
  worksheet.getRow(1).height = firstRowHeight;
  worksheet.getRow(2).height = secondRowHeight;

  // Define os estilos do cabeçalho
  const cell = worksheet.getCell("A2");
  cell.value = title;
  cell.fill = fillGreen;
  cell.font = fontTitleHeader;
}

/** Monta os detalhes do relatório presentes na "coluna" esquerda
 * abaixo do cabeçalho.
 * @param {Worksheet} worksheet Instância da planilha a ser exportada
 */
export function createDetailsColumn1(worksheet: Worksheet) {
  // Adiciona estilo da borda
  worksheet.addConditionalFormatting({
    ref: "A3:G10",
    rules: borderExpressionStyle,
  });

  // Adiciona estilo no título da coluna
  worksheet.mergeCells("A4:G4");
  worksheet.getCell("A4").fill = fillDarkGray;
  worksheet.getCell("A4").font = fontToDarkFill;

  // Adiciona estilo no bloco endereço do projeto
  worksheet.getCell("A6").value = labels.address;
  worksheet.getCell("A6").border = borderThickStyle;
  worksheet.getCell("A6").font = fontBold;
  worksheet.getCell("A6").fill = fillGray;

  worksheet.mergeCells("B6:D6");
  worksheet.getCell("B6").fill = fillGray;

  worksheet.mergeCells("A7:D7");
  worksheet.getCell("A7").fill = fillGray;

  // Adiciona estilo no bloco de Mês base do plano de custo
  worksheet.mergeCells("A9:B9");
  worksheet.getCell("A9").value = labels.baseMonth;
  worksheet.getCell("A9").border = borderThickStyle;
  worksheet.getCell("A9").fill = fillGray;
  worksheet.getCell("A9").font = fontBold;

  worksheet.mergeCells("C9:D9");
  worksheet.getCell("C9").fill = fillGray;

  // Adiciona estilo no bloco de Prazo da Obra do plano de custo
  worksheet.mergeCells("A10:B10");
  worksheet.getCell("A10").value = sideMenuLabels.monthReleaseFocus;
  worksheet.getCell("A10").border = borderThickStyle;
  worksheet.getCell("A10").fill = fillGray;
  worksheet.getCell("A10").font = fontBold;

  worksheet.mergeCells("C10:D10");
  worksheet.getCell("C10").fill = fillGray;

  // Adiciona estilo ao bloco Área do Terreno do plano de custo
  worksheet.getCell("F6").value = sideMenuLabels.landAreaFocus;
  worksheet.getCell("F6").border = borderThickStyle;
  worksheet.getCell("F6").fill = fillGray;
  worksheet.getCell("F6").font = fontBold;
  worksheet.getCell("G6").fill = fillGray;

  // Adiciona estilo ao bloco Unidades Privativas do plano de custo
  worksheet.getCell("F7").value = sideMenuLabels.privateUnitsFocus;
  worksheet.getCell("F7").border = borderThickStyle;
  worksheet.getCell("F7").fill = fillGray;
  worksheet.getCell("F7").font = fontBold;
  worksheet.getCell("G7").fill = fillGray;

  // Adiciona estilo ao bloco Área construída do plano de custo
  worksheet.getCell("F8").value = sideMenuLabels.builtAreaFocus;
  worksheet.getCell("F8").border = borderThickStyle;
  worksheet.getCell("F8").fill = fillGray;
  worksheet.getCell("F8").font = fontBold;
  worksheet.getCell("G8").fill = fillGray;

  // Adiciona estilo ao bloco Área equivalente do plano de custo
  worksheet.getCell("F9").value = sideMenuLabels.equivalentAreaFocus;
  worksheet.getCell("F9").border = borderThickStyle;
  worksheet.getCell("F9").fill = fillGray;
  worksheet.getCell("F9").font = fontBold;
  worksheet.getCell("G9").fill = fillGray;

  // Adiciona estilo ao bloco Área privativa do plano de custo
  worksheet.getCell("F10").value = sideMenuLabels.privateAreaFocus;
  worksheet.getCell("F10").border = borderThickStyle;
  worksheet.getCell("F10").fill = fillGray;
  worksheet.getCell("F10").font = fontBold;
  worksheet.getCell("G10").fill = fillGray;
}

/** Monta os detalhes do relatório presentes na "coluna" direita
 * abaixo do cabeçalho.
 * @param {Worksheet} worksheet Instância da planilha a ser exportada
 * @param {string} finalColumn Coluna final que será preenchida com o cabeçalho do relatório
 * @param {string} totalLabel Label que irá substituir, se preenchido, o label 'Custo Total'
 * @param {boolean} showBDI Define se será ou não exibido o total do BDI no relatório
 */
export function createDetailsColumn2(
  worksheet: Worksheet,
  finalColumn: string,
  totalLabel: string,
  showBDI: boolean = false
) {
  const shortReport = finalColumn === "J";
  let titleFinalColumn = shortReport ? "J" : "L";

  // Adiciona estilo da borda
  worksheet.addConditionalFormatting({
    ref: `H1:${finalColumn}11`,
    rules: borderExpressionStyle,
  });

  // Mescla linha em branco
  worksheet.mergeCells(`I6:${titleFinalColumn}6`);

  // Adiciona estilo no título do bloco custo total
  worksheet.mergeCells(`I4:${titleFinalColumn}4`);
  worksheet.getCell(`I4`).value = totalLabel || labels.total;
  worksheet.getCell(`I4`).fill = fillDarkGray;
  worksheet.getCell(`I4`).font = fontToDarkFill;

  // Adiciona estilo no bloco do custo total
  worksheet.mergeCells(`I5:${titleFinalColumn}5`);
  worksheet.getCell(`I5`).fill = fillGray;
  worksheet.getCell(`I5`).alignment = { horizontal: "center" };
  worksheet.getCell(`I5`).numFmt = monetaryFormat;

  // Adiciona estilo no título do bloco custo por área
  worksheet.mergeCells(`I7:${titleFinalColumn}7`);
  worksheet.getCell(`I7`).value = labels.areaCost;
  worksheet.getCell(`I7`).fill = fillDarkGray;
  worksheet.getCell(`I7`).font = fontToDarkFill;

  // Adiciona estilo no bloco do custo por área construída
  worksheet.getCell(`I8`).value = sideMenuLabels.builtAreaFocus;
  worksheet.getCell(`I8`).border = borderThickStyle;
  worksheet.getCell(`I8`).fill = fillGray;
  worksheet.getCell(`I8`).font = fontBold;

  if (!shortReport) worksheet.mergeCells(`J8:L8`);
  worksheet.getCell(`J8`).fill = fillGray;

  // Adiciona estilo no bloco do custo por área equivalente
  worksheet.getCell(`I9`).value = sideMenuLabels.equivalentAreaFocus;
  worksheet.getCell(`I9`).border = borderThickStyle;
  worksheet.getCell(`I9`).fill = fillGray;
  worksheet.getCell(`I9`).font = fontBold;

  if (!shortReport) worksheet.mergeCells(`J9:L9`);
  worksheet.getCell(`J9`).fill = fillGray;

  // Adiciona estilo no bloco do custo por área privativa
  worksheet.getCell(`I10`).value = sideMenuLabels.privateAreaFocus;
  worksheet.getCell(`I10`).border = borderThickStyle;
  worksheet.getCell(`I10`).fill = fillGray;
  worksheet.getCell(`I10`).font = fontBold;

  if (!shortReport) worksheet.mergeCells(`J10:L10`);
  worksheet.getCell(`J10`).fill = fillGray;

  // Adiciona estilos das células que exibiram os dados do Total BDI do orçamento
  if (showBDI) {
    worksheet.mergeCells(`N4:O4`);
    worksheet.getCell(`N4`).value = labels.totalBdi;
    worksheet.getCell(`N4`).fill = fillDarkGray;
    worksheet.getCell(`N4`).font = fontToDarkFill;

    worksheet.mergeCells(`N5:O5`);
    worksheet.getCell(`N5`).fill = fillGray;
    worksheet.getCell(`N5`).alignment = { horizontal: "center" };
    worksheet.getCell(`N5`).numFmt = monetaryFormat;
  }
}

/** Monta o cabeçalho do corpo do relatório com os nomes das colunas do orçamento.
 * @param {Worksheet} worksheet Instância da planilha a ser exportada
 * @param {string} finalColumn Coluna final que será preenchida com o cabeçalho do relatório
 */
export function createTitleRow(worksheet: Worksheet, finalColumn: string) {
  // Adiciona estilo da borda
  worksheet.addConditionalFormatting({
    ref: `A11:${finalColumn}11`,
    rules: borderExpressionStyle,
  });

  // Adiciona estilos da células com os títulos das propriedades
  // obrigatórias do orçamento
  const letters = alphabet.slice(0, alphabet.indexOf(finalColumn) + 1);
  for (const letter of letters) {
    worksheet.getCell(`${letter}12`).fill = fillDarkGray;
    worksheet.getCell(`${letter}12`).font = fontToDarkFill;
  }
}

/** Adiciona as propriedades customizadas do plano de custo ao cabeçalho do
 * corpo do relatório. */
export function addCustomPropertiesColumns(
  worksheet: Worksheet,
  initialColumn: string
) {
  const customProps = CustomPropertyStore.getWBSCustomProperties();
  const letters = alphabet.slice(alphabet.indexOf(initialColumn) + 1);
  letters.push(...createAlphabet());

  if (!customProps) return;

  // Adiciona estilo da borda
  worksheet.addConditionalFormatting({
    ref: `A11:${letters[customProps.length - 1]}12`,
    rules: borderExpressionStyle,
  });

  // Adiciona estilo na borda das linhas acima do cabeçalho do corpo
  // do relatório
  worksheet.addConditionalFormatting({
    ref: `${letters[0]}1:${letters[customProps.length - 1]}10`,
    rules: borderExpressionStyle,
  });

  // Define estilo da fonte e cor de fundo nas células das propriedades
  // personalizadas do plano de custo
  for (let i = 0; i < customProps.length; i++) {
    worksheet.getCell(letters[i] + "12").fill = fillDarkGray;
    worksheet.getCell(letters[i] + "12").font = fontToDarkFill;
  }
}

/** Define a largura das colunas do corpo do relatório. */
function setColumnsWidth(worksheet: Worksheet, finalColumn: string) {
  worksheet.getColumn(1).width = 12.01 * columnsOffset;
  worksheet.getColumn(2).width = 5.76 * columnsOffset;
  worksheet.getColumn(3).width = 12.01 * columnsOffset;
  worksheet.getColumn(4).width = 5.76 * columnsOffset;
  worksheet.getColumn(5).width = 12.01 * columnsOffset;
  worksheet.getColumn(6).width = 18.26 * columnsOffset;
  worksheet.getColumn(7).width = 12.01 * columnsOffset;
  worksheet.getColumn(8).width = 12.01 * columnsOffset;
  worksheet.getColumn(9).width = 15.01 * columnsOffset;
  worksheet.getColumn(10).width = 5.76 * columnsOffset;

  if (finalColumn <= "J") return;

  worksheet.getColumn(11).width = 5.76 * columnsOffset;
  worksheet.getColumn(12).width = 11.76 * columnsOffset;

  if (finalColumn <= "L") return;

  worksheet.getColumn(13).width = 11.76 * columnsOffset;
  worksheet.getColumn(14).width = 11.76 * columnsOffset;
  worksheet.getColumn(15).width = 12.76 * columnsOffset;
  worksheet.getColumn(16).width = 11.76 * columnsOffset;
  worksheet.getColumn(17).width = 12.76 * columnsOffset;

  return worksheet;
}

/** Define o alinhamento vertical das células no corpo do relatório. */
function setVerticalAlignment(worksheet: Worksheet) {
  for (let i = 1; i < 13; i++) {
    worksheet.getCell("A" + i).alignment = { vertical: "middle" };
    worksheet.getCell("B" + i).alignment = { vertical: "middle" };
    worksheet.getCell("C" + i).alignment = { vertical: "middle" };
    worksheet.getCell("D" + i).alignment = { vertical: "middle" };
    worksheet.getCell("E" + i).alignment = { vertical: "middle" };
    worksheet.getCell("F" + i).alignment = { vertical: "middle" };
    worksheet.getCell("G" + i).alignment = { vertical: "middle" };
    worksheet.getCell("H" + i).alignment = { vertical: "middle" };
    worksheet.getCell("I" + i).alignment = { vertical: "middle" };
    worksheet.getCell("J" + i).alignment = { vertical: "middle" };
    worksheet.getCell("K" + i).alignment = { vertical: "middle" };
    worksheet.getCell("L" + i).alignment = { vertical: "middle" };
    worksheet.getCell("M" + i).alignment = { vertical: "middle" };
    worksheet.getCell("N" + i).alignment = { vertical: "middle" };
    worksheet.getCell("O" + i).alignment = { vertical: "middle" };
    worksheet.getCell("P" + i).alignment = { vertical: "middle" };
    worksheet.getCell("Q" + i).alignment = { vertical: "middle" };
    worksheet.getCell("R" + i).alignment = { vertical: "middle" };
    worksheet.getCell("S" + i).alignment = { vertical: "middle" };
  }
}

/** Adiciona os dados do projeto e plano de custo abaixo do cabeçalho do relatório. */
function setCostPlanData(
  worksheet: Worksheet,
  companyName: string,
  showBDI: boolean
) {
  const development = DevelopmentStore.developmentSelected.data;
  const projectModel = WBSPropertiesStore.WBSProjectDataModel;
  const total = WBSStore.WBS.data.totalCost;
  const totalBdi = WBSStore.WBS.data.totalSale;

  // Adiciona o endereço do projeto
  if (development.address) {
    let arraySplit = development.address.split(",", 2);
    worksheet.getCell("B6").value = arraySplit[0] ? arraySplit[0] : "";
    worksheet.getCell("A7").value = arraySplit[1] ? arraySplit[1] : "";
  }

  // Adiciona o nome da empresa e do projeto
  worksheet.getCell("A4").value = companyName
    ? companyName + " / " + development.name
    : development.name;

  // Adiciona os totais do orçamento
  worksheet.getCell("I5").value = total;
  if (showBDI) worksheet.getCell("N5").value = totalBdi;

  // Adiciona os valores das propriedades do plano de custo
  if (projectModel) {
    const month = `0${projectModel.baseMonth.getMonth() + 1}`.slice(-2);
    const baseMonth = `${month}/${projectModel.baseMonth.getFullYear()}`;

    worksheet.getCell("G6").value = Number(projectModel.landArea) || "";
    worksheet.getCell("G7").value = Number(projectModel.privateUnits) || "";
    worksheet.getCell("C9").value = baseMonth;
    worksheet.getCell("C10").value = Number(projectModel.projectTerm) || "";

    const builtArea = Number(projectModel.builtArea || 0);
    worksheet.getCell("G8").value = builtArea || "";
    worksheet.getCell("J8").numFmt = monetaryFormat;
    worksheet.getCell("J8").value = builtArea > 0 ? total / builtArea : 0;

    const equivalentArea = Number(projectModel.equivalentArea || 0);
    if (equivalentArea) worksheet.getCell("G9").value = equivalentArea || "";
    worksheet.getCell("J9").numFmt = monetaryFormat;
    worksheet.getCell("J9").value =
      equivalentArea > 0 ? total / equivalentArea : 0;

    const privateArea = Number(projectModel.privateArea || 0);
    if (projectModel.privateArea)
      worksheet.getCell("G10").value = privateArea || "";
    worksheet.getCell("J10").numFmt = monetaryFormat;
    worksheet.getCell("J10").value = privateArea > 0 ? total / privateArea : 0;
  }
}

/** Popula os dados no corpo do relatório de acordo com o tipo do relatório. */
function populateTable(
  worksheet: Worksheet,
  resourceType: EReportType,
  isType?: boolean,
  propertyKey?: string
) {
  if (resourceType === EReportType.EAP) {
    getDataEAP(worksheet);
  } else if (resourceType === EReportType.RESOURCE) {
    getDataResource(worksheet);
  } else if (resourceType === EReportType.FORMATING) {
    getDataFormating(worksheet);
  } else if (resourceType === EReportType.TAKEOFF) {
    getDataTakeoff(worksheet);
  } else if (resourceType === EReportType.EAP_ANALYTICAL) {
    getDataEAPAnalytical(worksheet);
  } else if (resourceType === EReportType.GROUPED_RESOURCES_SYNTHETIC) {
    getDataGroupedResourcesSynthetic(worksheet, isType, propertyKey);
  } else if (resourceType === EReportType.GROUPED_RESOURCES_ANALYTICAL) {
    getDataGroupedResourcesAnalytic(worksheet, isType, propertyKey);
  } else if (resourceType === EReportType.CURVE_ABC_SYNTHETIC) {
    getDataToSyntheticABCCurveServiceReport(worksheet);
  } else if (resourceType === EReportType.CURVE_ABC_ANALYTICAL) {
    getDataToAnalyticABCCurveServiceReport(worksheet);
  }
}

/** Baixa imagem da Empresa, converte para base64 e adiciona no relatório. */
async function addCompanyImage(
  workbook: Workbook,
  worksheet: Worksheet,
  imageURL: string
) {
  if (!imageURL) return;

  const fileBase64 = await fetch(imageURL)
    .then((response) => response.blob())
    .then((blob) => readAsDataURL(blob));

  if (fileBase64 && typeof fileBase64 === "string")
    addImageToWorksheet(workbook, worksheet, fileBase64, "A1:C1");
}

/** Adiciona e posiciona a imagem na planilha. */
function addImageToWorksheet(
  workbook: Workbook,
  worksheet: Worksheet,
  image: string,
  cells: string
) {
  let imageId = workbook.addImage({
    base64: image,
    extension: "png",
  });

  worksheet.addImage(imageId, cells);
}

function readAsDataURL(file: Blob) {
  return new Promise(
    (resolve: (value: string | ArrayBuffer | null) => void) => {
      let fr = new FileReader();

      fr.onloadend = function () {
        resolve(fr.result);
      };
      fr.readAsDataURL(file);
    }
  );
}

/** Gera o arquivo Excel e inicia o download na máquina local. */
function saveAs(workbook: Workbook, name: string) {
  const blobType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

  workbook.xlsx.writeBuffer().then((data) => {
    const blob = new Blob([data], { type: blobType });
    FileSaver.saveAs(blob, name);
  });
}



// WEBPACK FOOTER //
// ./src/modules/costPlan/assets/excelReports/export.ts